import type { MeasurementSubject } from "@workspace/api-zod/measurement-review";
import type { QuoteDimension } from "@workspace/api-zod/quantity-dimensions";

const stop = new Set("opening openings wall walls width height length depth projection size beam beams steel ub uc the a an of for to in with at from ground floor internal work works section drawing maximum minimum max min".split(" "));
function tokens(text: string) {
  return new Set(text.toLowerCase().replace(/\balter(?:ed|ation)?\b/g, "alter")
    .split(/[^a-z0-9]+/).filter(t => t && !stop.has(t) && !/^\d+$/.test(t)));
}
function score(a: string, b: string) {
  const left = tokens(a), right = tokens(b);
  return [...left].reduce((sum, t) => sum + (right.has(t) ? /^(new|alter|doorway|kitchen|dining|chimney)$/.test(t) ? 3 : 1 : 0), 0);
}
function uniqueMatch<T>(items: T[], text: string, name: (t: T) => string): T | undefined {
  const ranked = items.map(item => ({ item, score: score(text, name(item)) })).sort((a, b) => b.score - a.score);
  return ranked[0]?.score >= 2 && ranked[0].score > (ranked[1]?.score || 0) ? ranked[0].item : undefined;
}

/** Recover missing links from scoped evidence or explicit work clauses, never by list position. */
export function recoverSubjectLinks(subjects: MeasurementSubject[], evidence: QuoteDimension[], inferred: MeasurementSubject[]) {
  const result = subjects.map(s => ({ ...s, dimensionEvidenceIds: [...s.dimensionEvidenceIds] }));
  const claimed = new Set(result.flatMap(s => s.dimensionEvidenceIds));
  const add = (subject: MeasurementSubject | undefined, ids: string[]) => {
    if (!subject) return;
    for (const id of ids) if (!claimed.has(id)) { subject.dimensionEvidenceIds.push(id); claimed.add(id); }
  };
  for (const e of evidence) {
    if (claimed.has(e.id)) continue;
    const text = `${e.label} ${e.note}`;
    let target = uniqueMatch(result, text, s => s.label);
    if (!target) {
      const kinds = result.filter(s => s.kind === "chimney" ? /\bchimney\b/i.test(e.label) :
        s.kind === "opening" ? /\b(opening|doorway)\b/i.test(e.label) :
        new RegExp(`\\b${s.kind}\\b`, "i").test(e.label));
      if (kinds.length === 1) target = kinds[0];
    }
    add(target, [e.id]);
  }
  for (const hint of inferred) {
    add(uniqueMatch(result.filter(s => s.kind === hint.kind), hint.label, s => s.label), hint.dimensionEvidenceIds);
  }
  return result;
}

/** Naming only: never turn member cross-section numbers into opening widths/heights. */
export function subjectRecognitionLabel(subject: MeasurementSubject, context: string[], evidence: QuoteDimension[] = []) {
  let beam = subject.beamSize;
  if (!beam && subject.kind === "opening") {
    const lines = context.join("\n").split(/\n/);
    const clause = uniqueMatch(lines, subject.label, line => line);
    const sectionPattern = /\b\d{2,4}\s*[x×]\s*\d{2,4}\s*[x×]\s*\d+(?:\.\d+)?\s*(?:UB|UC|RHS|SHS)\b/gi;
    const hinted = subject.label.match(/\b(\d{2,4})?\s*(UB|UC|RHS|SHS)\b/i);
    const widths = evidence.filter(e => subject.dimensionEvidenceIds.includes(e.id) && e.unit === "m" &&
      /\b(opening|doorway)\b/i.test(e.label) && /\b(width|span)\b/i.test(e.label)).map(e => e.value);
    const widthClauses = lines.filter(line => /\b(opening|doorway)\b/i.test(line) &&
      [...line.matchAll(/(\d+(?:\.\d+)?)\s*(mm|m)\b/gi)].some(match =>
        widths.some(width => Math.abs(width - Number(match[1]) / (match[2].toLowerCase() === "mm" ? 1000 : 1)) < 1e-8)));
    const matchingSizes: string[] = clause?.match(sectionPattern) ||
      widthClauses.flatMap(line => [...line.matchAll(sectionPattern)].map(match => match[0]));
    if (!matchingSizes.length && hinted) matchingSizes.push(...(context.join("\n").match(sectionPattern) || []));
    const candidates = matchingSizes.filter(size => !hinted || new RegExp(`\\b${hinted[2]}$`, "i").test(size) &&
        (!hinted[1] || size.startsWith(hinted[1])));
    const unique = [...new Set(candidates.map(size => size.replace(/\s*[x×]\s*/g, " × ").replace(/\s+/g, " ").trim()))];
    if (unique.length === 1) beam = unique[0];
  }
  const compact = (s: string) => s.toLowerCase().replace(/[\s×x]/g, "");
  return beam && !compact(subject.label).includes(compact(beam))
    ? `${subject.label.slice(0, 177 - beam.length)} — ${beam}` : subject.label;
}
